/**
 * The English catalogue, and the shape every other one has to match.
 *
 * This file is the SOURCE OF TRUTH in two senses. It is what a reader sees when
 * a translation is missing - i18next falls back key by key, not file by file -
 * and it is the TYPE the other catalogues are declared against, so a key
 * dropped here or missing there is a `tsc` error rather than a blank label
 * somebody finds in production.
 *
 * Sections follow the screens. `common` is only for words that genuinely mean
 * the same thing everywhere: "Save" as a button is `common.save`, but a status
 * that happens to read "Free" belongs to the bench section, because Polish and
 * Finnish decline it differently there than they would as a verb.
 *
 * Plurals use i18next's suffixes (`_one`, `_few`, `_many`, `_other`) and are
 * resolved by `Intl.PluralRules`, which is the only way to get Polish right:
 * 1 minuta, 2-4 minuty, 5+ minut, and 22 minuty again.
 */
const en = {
  common: {
    loading: "loading…",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    close: "Close",
    delete: "Delete",
    edit: "Edit",
    retry: "Try again",
    search: "Search",
    filter: "Filter",
    all: "All",
    none: "none",
    unknown: "unknown",
    yes: "yes",
    no: "no",
    more: "More",
    back: "Back",
    copy: "Copy",
    copied: "Copied",
    dash: "—",
    crumbs: "Where this page is",
  },

  nav: {
    campaigns: "Campaigns",
    account: "Account",
    now: "Now",
    projects: "Projects",
    benches: "Benches",
    runs: "Runs",
    reports: "Reports",
    quality: "Quality",
    manual: "Manual",
    people: "People",
    more: "More",
    menu: "Menu",
    main: "main",
    signOut: "Sign out",
    readOnly: "read-only",
    readOnlyTitle: "read only",
    admin: "admin",
    adminTitle: "instance administrator",
    noPassword: "no password set",
    notFound: "No such page.",
  },

  theme: {
    label: "Colour scheme",
    light: "Light",
    auto: "Follow the system",
    dark: "Dark",
  },

  lang: {
    uncheckedFi: "This Finnish was written without a Finnish speaker and has not been checked. If something reads wrong, please say so — it can be fixed.",
    label: "Language",
  },

  time: {
    justNow: "just now",
    minutesAgo_one: "{{count}} min ago",
    minutesAgo_other: "{{count}} min ago",
    hoursAgo_one: "{{count}} h ago",
    hoursAgo_other: "{{count}} h ago",
    daysAgo_one: "{{count}} day ago",
    daysAgo_other: "{{count}} days ago",
    days: "Sun Mon Tue Wed Thu Fri Sat",
    months: "Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec",
    /** "9 Sep, 06:25" - the order of day and month, so a catalogue can put the
     *  month first without the formatter knowing which language it is in. */
    onDate: "{{day}} {{month}}, {{time}}",
    onDateWithYear: "{{day}} {{month}} {{year}}, {{time}}",
    onDay: "{{day}} {{month}} {{year}}",
    durationHours: "{{h}}h {{m}}m",
    durationMinutes: "{{m}}m {{s}}s",
    durationSeconds: "{{s}}s",
  },

  login: {
    title: "Sign in",
    heading: "HIL",
    prompt: "Sign in to see the benches.",
    noPassword: "This deployment has no password configured.",
    username: "Username",
    password: "Password",
    submit: "Sign in",
    submitting: "Signing in…",
  },

  password: {
    intro:
      "You signed in with the temporary password. Replace it before going on — nothing else can be changed until you do.",
    title: "Change password",
    heading: "Choose a password",
    current: "Temporary password",
    next_one: "New password (at least {{count}} character)",
    next_other: "New password (at least {{count}} characters)",
    again: "New password again",
    tooShort_one: "{{count}} more character",
    tooShort_other: "{{count}} more characters",
    same: "must differ from the temporary one",
    mismatch: "does not match",
    submit: "Set password",
  },

  roles: {
    name: {
      guest: "guest",
      operator: "operator",
      maintainer: "maintainer",
      owner: "owner",
    },
    means: {
      guest: "reads runs, reports and configuration",
      operator: "takes the lease and queues runs — moves the hardware",
      maintainer: "configures benches, rotates tokens, deletes runs",
      owner: "decides who else is in",
    },
  },

  config: {
    help: {
      HIL_SSH: "SSH address of the screen under test",
      HIL_DUT_HOST: "its address on its own",
      HIL_API_BASE: "AO2025 API root",
      HIL_KVM: "JetKVM address",
      HIL_MODEM_HOST: "modem management interface",
      HIL_SIVMOCK: "the mock SIV this bench uses",
      HIL_CAPS: "declared capabilities",
    },
  },

  scope: {
    notRecorded: "which tests: not recorded",
    notRecordedTitle:
      "recorded before the selection was saved - it may have been a whole suite or a slice",
    slice: "part of the tests",
    sliceTitle: "part of the tests, not all of them",
    matching: "only tests matching “{{filter}}”",
    onlyFile: "only {{file}}",
    marker: "marker: {{marker}}",
    files: "files: {{files}}",
  },

  build: {
    notRecorded: "build not recorded",
    notRecordedTitle: "this run predates the harness reporting its build",
  },

  verdict: {
    running: "running",
    noVerdicts: "no verdicts recorded",
    nothingRecorded: "nothing recorded",
    clean: "clean",
    failed_one: "{{count}} failed",
    failed_other: "{{count}} failed",
    passed_one: "{{count}} passed",
    passed_other: "{{count}} passed",
    naHere_one: "{{count}} not run here",
    naHere_other: "{{count}} not run here",
    xfailed_one: "{{count}} expected failure",
    xfailed_other: "{{count}} expected failures",
    barLabel:
      "{{failed}} failed, {{passed}} passed, {{skipped}} not run here, {{xfailed}} expected failures",
    legend: {
      failed: "failed",
      xfailed: "expected failure",
      skipped: "not run here",
      passed: "passed",
    },
  },

  run: {
    steps_one: "{{count}} step",
    steps_other: "{{count}} steps",
  },

  caps: {
    heading: "Capabilities",
    none:
      "No run has reported yet, so there is nothing to show. A bench's capabilities are worked out while a run starts, not configured.",
    noDescription: "no description was served for this one",
    lastRun: "the last run",
    reportedBy_one:
      "{{count}} reported by <runLink>{{run}}</runLink> · worked out by probing the bench at start-up, not read from the configuration",
    reportedBy_other:
      "{{count}} reported by <runLink>{{run}}</runLink> · worked out by probing the bench at start-up, not read from the configuration",
  },

  live: {
    onBench: "on <benchLink>{{bench}}</benchLink>",
    step: "step {{no}}",
    stepOf: "step {{no}} of {{total}}",
    unknownStep: "?",
    starting: "starting — harness self-tests",
    elapsedSeconds: "{{s}}s",
    elapsedMinutes: "{{m}} min",
    elapsedHours: "{{h}} h",
  },

  configPanel: {
    heading: "Configuration",
    edit: "Edit configuration",
    nothingSet:
      "Nothing set — the bench uses the defaults built into <code>run.sh</code>.",
    note:
      "Read at the start of every run. A value passed on the command line still wins, and a bench with no entry here uses <code>run.sh</code>'s defaults. <b>No passwords or API keys</b> — those stay on the bench. Anyone who can read this bench can read every value here.",
    capsNone: "none declared",
    capsAdd: "add a capability and press Enter",
    capsRemove: "remove {{cap}}",
  },

  members: {
    heading: "Members ({{count}})",
    add: "Add member",
    needOwner: "you need the owner role here to change this",
    emptyBench:
      "Nobody is granted anything on this bench alone — it is reached through its project.",
    emptyProject: "Nobody yet. Grant a person or a group a role here.",
    roleFor: "role for {{name}}",
    remove: "remove",
    inherited: "Inherited from the project ({{count}})",
    inheritedNote: "Change these on the project. A grant made here can only add.",
    kind: { user: "Person", group: "Group" },
    kindPersonTitle: "one person",
    kindGroupTitle: "a group of people",
    deactivated: "deactivated",
    roleKeySummary: "what these roles mean",
    grantTo: "Grant to",
    subjectKind: "subject kind",
    aPerson: "a person",
    aGroup: "a group",
    person: "Person",
    group: "Group",
    subject: "subject",
    choose: "choose…",
    role: "Role",
    addButton: "Add",
  },

  auto: {
    title: "Automatic testing",
    stalled: "The bench has not reported since {{when}}, so nothing has started on its own since then.",
    sameProduct: "{{build}} appeared {{when}} with the same product build ({{product}}) as one already tested, so it started no campaign.",
    off: "This bench tests nothing on its own.",
    summary: "This bench runs {{what}}.",
    onNewBuild: "a campaign for every new build",
    nightlyAt: "a campaign every night at {{time}}",
    and: " and ",
    newBuildLabel: "Run a campaign for every new build that appears on this bench",
    nightlyLabel: "Run a campaign every night at",
    nightlyTime: "Nightly time",
    localTime: "your local time",
    cost: "Each campaign holds the bench until every profile has run. It never starts while somebody's work is queued or running, or while the bench is held; it waits instead.",
    save: "Save",
    saved: "Saved.",
    waiting: "Waiting to start {{what}} ({{build}}): {{why}}.",
    last: "Last started on its own: <campaignLink>{{what}} ({{build}})</campaignLink>, {{when}}.",
    missed: "The nightly campaign of {{day}} did not run: {{why}}.",
    installed: "the installed build",
    origin: {
      new_build: "the campaign for a new build",
      nightly: "the nightly campaign",
      manual: "a campaign",
    },
  },
  likeness: {
    flat: "The same score in every run: a steady difference, not flicker.",
    title: "Likeness to {{golden}}, run by run",
    aria: "Likeness to {{golden}} over {{count}} runs, with the threshold",
    threshold: "threshold {{value}}",
    point: "{{run}}: {{score}} (threshold {{threshold}})",
    table: "The numbers",
    passed: "passed",
    missed: "missed",
    near: "Passed close to its threshold: {{score}} against {{threshold}}, {{gap}} to spare.",
  },
  product: {
    choose: "Choose a product",
    panel: "Products",
    all: "All products",
    list: "List of projects",
    hint: "Each card says what the product's newest result is.",
    tabs: "In this product",
    tabOverview: "Overview",
    tabBuilds: "Builds",
    tabTests: "Tests",
    tabBenches: "Benches",
    tabSettings: "Settings",
    search: "Search",
    searchPlaceholder: "A product, bench, page or run",
    noHits: "Nothing matches.",
    kind: { product: "product", page: "page", bench: "bench", run: "run", build: "build", case: "case" },
    noBenches: "no bench yet",
    online: "benches online: {{online}} of {{total}}",
    offlineFor_one: "bench offline for {{count}} day",
    offlineFor_other: "bench offline for {{count}} days",
    offline: "bench offline",
    installed: "the installed build",
    campRunning: "{{build}}: a campaign is running",
    campGreen: "{{build}}: everything planned ran and passed",
    campKnown: "{{build}}: nothing new failed; known problems: {{n}}",
    campFailed_one: "{{build}}: {{count}} test failed",
    campFailed_other: "{{build}}: {{count}} tests failed",
    campLost: "{{build}}: a run never reported its results",
    campShort: "{{build}}: not everything planned ran",
    runFailed_one: "{{run}}: {{count}} test failed",
    runFailed_other: "{{run}}: {{count}} tests failed",
    runClean: "{{run}}: nothing failed",
    nothingYet: "nothing has run yet",
    empty: "Without a bench or a run yet:",
  },
  builds: {
    listTitle: "Builds",
    title: "Build {{build}}",
    back: "builds",
    listAnswer_one: "{{count}} build, the newest {{build}}.",
    listAnswer_other: "{{count}} builds, the newest {{build}}.",
    listAfter: "A build is the version of the product under test, not the bench's image. Open one for its campaigns and its other runs.",
    listNone: "No run of this product has said which build it tested.",
    listNoneAfter: "Runs are below. Once each run records its build (step K0 for this product), they group into builds here.",
    counts: "{{runs}} runs, {{campaigns}} campaigns",
    unbuiltTitle_one: "{{count}} run with no build recorded",
    unbuiltTitle_other: "{{count}} runs with no build recorded",
    unbuiltNote: "Newest first; the newest 20 are listed.",
    notFound: "No build {{build}} in this product.",
    span: "{{runs}} runs, {{first}} – {{last}}",
    newestCampaign: "Newest campaign, #{{id}}",
    openCampaign: "Open the campaign",
    campaigns_one: "{{count}} campaign",
    campaigns_other: "{{count}} campaigns",
    campaignName: "Campaign #{{id}}",
    ranOf: "{{ran}} of {{planned}} ran",
    fullRuns_one: "{{count}} full run outside a campaign",
    fullRuns_other: "{{count}} full runs outside a campaign",
    slices_one: "{{count}} run on part of the tests",
    slices_other: "{{count}} runs on part of the tests",
    slicesNote: "Checks of a fix, recordings, trials. Each says only what its own cases say.",
    route: "The stops of the campaign, in order",
    stopLost: "no results arrived",
    stopKnown: "known {{n}}",
    stopFailed: "failed {{n}}",
    wRunning: "running",
    wGreen: "green",
    wKnown: "known problems",
    wFailed_one: "{{count}} failed",
    wFailed_other: "{{count}} failed",
    wLost: "no results",
    wShort: "not all ran",
    wNone: "nothing ran",
    wClean: "nothing failed",
    wNoResults: "no results",
    aRunning: "A campaign is running.",
    aGreen: "Everything planned ran, and nothing failed.",
    aKnown: "Nothing new failed. Known problems: {{n}}.",
    aFailed_one: "{{count}} test failed in the newest campaign.",
    aFailed_other: "{{count}} tests failed in the newest campaign.",
    aLost: "A run of the newest campaign never reported its results.",
    aShort: "Not everything planned ran in the newest campaign.",
    aNone: "Nothing has run on this build.",
    aRunFailed_one: "{{run}}: {{count}} test failed.",
    aRunFailed_other: "{{run}}: {{count}} tests failed.",
    aRunClean: "{{run}}: nothing failed.",
    startCampaign: "Start a campaign",
    report: "Make a report",
    images_one: "Bench image ({{count}})",
    images_other: "Bench images ({{count}})",
    telling: "Cases that say something",
    tellingNote: "in campaign #{{id}}",
    tellingNone: "Nothing failed there.",
    tellKnown: "known",
    tellFailed: "failed",
    open: "Open",
  },
  overview: {
    leadCampaign: "Campaign #{{id}}, {{when}}: {{ran}} of {{planned}} ran.",
    leadBuildRuns: "{{runs}} runs of this build, the newest {{when}}.",
    leadRun: "Newest full run, {{when}}, on {{bench}}",
    noBuild: "This product's runs do not record which build they tested yet (step K0), so this is its newest full run, not a build.",
    nothing: "Nothing has run on this product yet.",
    start: "Start testing",
    launch: "Build a new version",
    openBuild: "Open build {{build}}",
    newestCampaign: "Newest campaign of {{build}}",
    openCampaign: "Open the campaign",
    attention: "Needs a look",
    attentionNone: "Nothing needs a look.",
    aLost: "no results",
    aLostText: "{{run}} ({{stop}}) never reported its results",
    aQuiet: "quiet",
    aQuietText: "{{bench}} has not reported since {{when}}",
    open: "Open",
    bench: "Bench",
    benches_one: "{{count}} bench",
    benches_other: "{{count}} benches",
    allBenches: "Benches",
    heldBy: "held by {{actor}} {{purpose}}",
    settings: "Settings: what this product is, who works on it, what its run form offers",
  },
  productSettings: {
    back: "{{name}}",
    answer: "What this product is, who works on it, and what its run form offers.",
  },
  caseCtx: {
    coverageView: "Coverage",
    manualView: "By hand",
    quality: "Coverage of group {{group}}",
    stale: "The review is stale: the group's cases changed since somebody graded it.",
    manual: "By hand: about {{minutes}} min, {{priority}} priority",
    pre: "Before: {{text}}",
    expected: "Expect: {{text}}",
  },
  benchAdmin: {
    title: "Administration",
    note: "Rename the bench, give its agent a new token, or delete it.",
  },
  casePage: {
    crumb: "{{product}} › Tests",
    back: "tests",
    answerKnown: "Known problem until {{date}}.",
    answerNone: "Not run yet.",
    answerPassed: "Passed in {{run}}.",
    answerFailed: "Failed in {{run}}.",
    answerOther: "{{run}}: {{verdict}}.",
    expects: "What it expects, as compared in {{run}}",
    mask: "mask of {{areas}} areas, {{pct}}% of the screen",
    likeness: "Likeness to the golden, run by run",
    does: "What it does",
    group: "Group",
    steps: "Steps",
    requires: "Needs",
    coverage: "Coverage and by hand",
    grade: "Group grade",
    byHand: "By hand",
    byHandValue: "about {{minutes}} min, {{priority}} priority",
    expect: "Expect",
    noCoverage: "No review and no by-hand procedure for this case.",
    history: "Results, newest first",
    definition: "Definition, as written",
  },
  buildPicker: {
    title: "DisplayOS build and tests",
    explain: "Each image on the bench holds one DisplayOS build and the tests that run on it. The bench switches to the image you choose before the run; if it cannot, nothing runs and the job says why.",
    nowIs: "On the bench now: {{build}}, tests {{tests}}.",
    nowBy: "Switched there by",
    nowByRun: "Switched there by {{run}}, {{when}}.",
    nowCaveat: "A switch made by hand on the bench does not show here.",
    nowUnknown: "Which image the bench is on is not known here: no run from hil-central has switched it yet.",
    keep: "Keep what is on the bench",
    keepNow: "No switch: runs {{build}} with tests {{tests}}, as now.",
    keepWhy: "No switch. Runs whatever image the bench is on now.",
    tests: "Tests {{sha}}",
    scripts: "bench scripts {{sha}}",
    pointer: "{{tag}}",
    pointerWhy: "another name for one of the images above; which one is not known here",
    oldShape: "tagged the old way: which tests it holds is not in its name",
    newest: "newest · the nightly campaign uses this",
    onBenchNow: "on the bench now",
    olderTests: "older tests",
    download: "needs download · minutes",
    lastUsed: "last used {{when}}, {{what}}",
    neverUsed: "not used from hil-central yet",
    campaign: "campaign #{{id}}",
    olderWarning: "This image carries older tests than the newest one for the same DisplayOS build. The cases and the test data may differ from today's, so a difference in the results may come from the tests, not from DisplayOS.",
    registry_one: "{{count}} image in the registry (needs download)",
    registry_other: "{{count}} images in the registry (need download)",
    other_one: "{{count}} other name on the bench",
    other_other: "{{count}} other names on the bench",
    typeIt: "Type an image tag",
  },
  runAbout: {
    line_one: "Checks {{count}} group of cases: {{names}}.",
    line_other: "Checks {{count}} groups of cases: {{names}}.",
    lineMore_one: "Checks {{count}} group of cases: {{names}} and {{more}} more.",
    lineMore_other: "Checks {{count}} groups of cases: {{names}} and {{more}} more.",
    howReplay: "Plays back recorded OBA traffic, on {{layout}}.",
    howSimulated: "Drives {{layout}} with a simulated journey from the OBA Emulator.",
    howFixture: "Drives the harness's own test layout (hil_fixture) with a simulated journey from the OBA Emulator.",
    howOther: "Data mode {{mode}}, on {{layout}}.",
    theLayout: "the customer's layout",
    checks: "What it checks:",
    cases_one: "{{count}} case",
    cases_other: "{{count}} cases",
    failed_one: "{{count}} failed",
    failed_other: "{{count}} failed",
    more_one: "{{count}} more group",
    more_other: "{{count}} more groups",
    fewer: "fewer",
  },
  known: {
    status: "known",
    short: "Known problem until {{date}}.",
    shared_one: "{{count}} known problem: {{reason}}",
    shared_other: "{{count}} known problems, all for one reason: {{reason}}",
    title: "Known problems",
    intro: "Failing cases somebody has accounted for, until a date. They still fail on their runs; campaigns and Now count them apart, so a new failure stands out.",
    none: "No known problems.",
    onlyKnown: "Nothing new failed. Known problems: {{n}}.",
    onlyKnownNewest: "Nothing new failed in the newest campaign. Known problems: {{n}}.",
    also: "Known problems: {{n}}.",
    word: "known problems",
    onStop: "Known problems here: {{list}}.",
    chip: "Known problem until {{date}}: {{reason}}",
    by: "set by {{who}}",
    mark: "Mark as a known problem",
    reason: "Why it fails",
    until: "Until",
    save: "Save",
    cancel: "Cancel",
    end: "End it",
    stateOpen: "until {{date}}",
    stateExpired: "expired {{date}}",
    closedPassed: "ended {{date}}: the case passed",
    closedRemoved: "ended {{date}} by {{who}}",
  },
  campaigns: {
    title: "Campaigns",
    heading: "Campaigns",
    answerNone: "No campaign has run here yet.",
    answerGreen: "The newest campaign ran everything planned, and nothing failed.",
    answerRunning: "A campaign is running on {{bench}} now.",
    answerCancelled: "The newest campaign was cancelled before everything ran.",
    answerFailed_one: "{{count}} test failed in the newest campaign.",
    answerFailed_other: "{{count}} tests failed in the newest campaign.",
    answerNotAllRan: "The newest campaign ran, but not everything planned did.",
    answerLost: "A run of the newest campaign stopped before its tests reported.",
    loaded_one: "{{count}} campaign, newest first.",
    loaded_other: "{{count}} campaigns, newest first.",
    projectViews: "Which project",
    outcomeViews: "Which outcome",
    allProjects: "All projects",
    any: "Any outcome",
    onlyNotGreen: "Not green",
    onlyGreen: "Green",
    onlyRunning: "Running",
    oneBench: "Only the campaigns of {{bench}}.",
    showEveryBench: "Show every bench",
    empty: "No campaign has run here yet. A campaign runs every profile of a bench, one after another.",
    noneMatch: "No campaign here matches that.",
    buildGroup_one: "Build {{build}} · {{count}} campaign",
    buildGroup_other: "Build {{build}} · {{count}} campaigns",
    installedGroup_one: "Whatever was installed on {{bench}} · {{count}} campaign",
    installedGroup_other: "Whatever was installed on {{bench}} · {{count}} campaigns",
    rowTitle: "Campaign #{{id}} on {{bench}}",
    loadMore: "Show {{count}} more",
    filterNote_one: "Filtered on this page, over the {{count}} campaign loaded.",
    filterNote_other: "Filtered on this page, over the {{count}} campaigns loaded.",
    origin: {
      manual: "started by {{who}}",
      new_build: "started on its own, new build",
      nightly: "started on its own, at night",
    },
  },
  campaign: {
    badge: "Campaign #{{id}}",
    stops_one: "{{count}} stop",
    stops_other: "{{count}} stops",
    routeHeading: "Runs",
    why: "Why: {{reason}}",
    notFound: "There is no campaign #{{id}}. The campaigns page lists every one of them.",
    report: "Make a report of this campaign",
    origin: {
      new_build: "started on its own when a new build appeared, {{when}}",
      nightly: "started on its own at night, {{when}}",
    },
    loading: "Loading the campaign…",
    back: "← {{bench}}",
    title: "Campaign on {{bench}}",
    build: "build {{build}}",
    installed: "the build already installed",
    startedBy: "started by {{who}} {{when}}",
    answerGreen: "Everything planned ran, and nothing failed.",
    answerRunning: "Running {{n}} of {{of}}: {{label}}.",
    answerWaiting: "Waiting for the bench to start the next run.",
    answerCancelled: "Cancelled before everything ran.",
    answerFailed_one: "{{count}} test failed.",
    answerFailed_other: "{{count}} tests failed.",
    answerNotAllRan: "Nothing failed, but not everything planned ran.",
    answerLost: "A run stopped before its tests reported, so part of this campaign has no result.",
    stopLost: "This run stopped before its tests reported. Cases with a failed check: {{n}}.",
    imageDetails: "Bench image",
    planned_one: "{{count}} planned",
    planned_other: "{{count}} planned",
    ran_one: "{{count}} ran",
    ran_other: "{{count}} ran",
    passed_one: "{{count}} passed",
    passed_other: "{{count}} passed",
    expected_one: "{{count}} expected failure",
    expected_other: "{{count}} expected failures",
    failed_one: "{{count}} failed",
    failed_other: "{{count}} failed",
    notRun_one: "{{count}} not run",
    notRun_other: "{{count}} not run",
    routeLabel: "The runs of this campaign, in order",
    attempt: "attempt {{n}}",
    openRun: "Open the run",
    retry: "Run again",
    retried: "Queued again. The stop shows the new attempt.",
    cancel: "Cancel the campaign",
    cancelled_one: "Cancelled {{count}} run.",
    cancelled_other: "Cancelled {{count}} runs.",
    recent: "Recent campaigns on this bench",
    listItem: "Campaign {{when}}: {{ran}} of {{planned}} ran",
    state: {
      queued: "waiting",
      claimed: "starting",
      running: "running now",
      done: "finished",
      failed: "did not finish",
      cancelled: "cancelled",
      missing: "not queued",
    },
    status: {
      running: "running",
      green: "everything ran, nothing failed",
      not_green: "not all green",
      cancelled: "cancelled",
    },
  },
  start: {
    everyProfile_one: "Every profile: {{count}} run, {{cases}} cases",
    everyProfile_other: "Every profile: {{count}} runs, {{cases}} cases",
    everyProfileCost: "Holds the bench until every profile has run, one after another.",
    campaignStarted: "Campaign started. <campaignLink>Follow it as a route of its runs.</campaignLink>",
    title: "Start testing",
    bench: "Bench",
    benchOffline: "{{bench}} — not reporting",
    benchHeld: "{{bench}} — held by {{actor}}",
    benchReadOnly: "{{bench}} — you may only look",
    why: "Why",
    queued: "Queued on <benchLink>{{bench}}</benchLink>. The bench page shows it while it waits and runs.",
    none: "No bench to test on yet.",
    loading: "Loading benches…",
  },
  runPanel: {
    profile: "What to test",
    profileCases_one: "{{count}} case",
    profileCases_other: "{{count}} cases",
    moreOptions: "Advanced: choose the tests yourself",
    heading: "Start testing",
    markers: {
      auto: "auto — fully automated",
      screenshot: "screenshot_partial — visual checks",
      both: "auto + screenshot_partial",
    },
    running: "Running",
    queued: "Queued",
    askedBy: "asked by {{who}} {{when}}",
    stop: "Stop",
    stopTitle: "stopping a run needs the operator role here",
    stopping:
      "Asked the bench to stop. It ends the suite on its next check-in, within a minute.",
    removed: "Removed from the queue.",
    cancelNote:
      "Cancelling records the decision; it does not reach into the bench and stop the suite.",
    waiting: "Waiting ({{count}})",
    remove: "remove",
    leaseHeld:
      "<b>{{actor}}</b> is holding this bench. A run cannot start until the lease ends — the bench would refuse it anyway.",
    leaseHeldFor:
      "<b>{{actor}}</b> is holding this bench: {{purpose}}. A run cannot start until the lease ends — the bench would refuse it anyway.",
    tests: "Group of tests",
    jobMarked: "tests marked “{{marker}}”",
    jobMatching: "matching “{{filter}}”",
    jobBuild: "build {{build}}",
    onlyMatching: "Only matching",
    filterPlaceholder: "e.g. LED (optional)",
    reasonPlaceholder:
      "Why — the next person reads this, as with a lease purpose",
    queueing: "Starting…",
    start: "Start testing",
    needOperator:
      "Queueing a run moves the hardware; that needs the operator role on this bench.",
    takesDut:
      "Holds the bench for as long as the tests run: hours, for the full set.",
    installedVersion:
      "Tests the build already installed on this bench. For a version that is not built yet, open the <projectLink>project</projectLink> and build it first.",
    installedVersionNoProject:
      "Tests the build already installed on this bench. This bench is in no project, so there is nowhere to build a new version from.",
    buildRef: "Build to install first (optional)",
    buildRefPlaceholder: "leave empty to test the build already on the bench",
    buildRefList: "Already built: choosing one installs it, no build needed.",
    buildRefNote: "What is asked for; the run records what actually installed. If it cannot be installed the suite does not start and the job fails with the reason.",
    offline: "The bench agent is not reporting; nothing would pick this up.",
    earlier: "earlier runs asked for ({{count}})",
    jobState: {
      queued: "queued",
      claimed: "claimed",
      running: "running",
      done: "done",
      failed: "failed",
      cancelled: "cancelled",
    },
  },

  markdown: {
    empty: "Nothing written here yet.",
    showAll: "Show all",
    showLess: "Show less",
    label: "Markdown",
    tabWrite: "write",
    tabSplit: "split",
    tabPreview: "preview",
    placeholder: "# Heading\n\nAddresses, quirks, what this is wired to…",
    previewEmpty: "Nothing to preview yet.",
    dirty: "unsaved changes",
    saved: "saved",
    shortcuts: "⌘/Ctrl+Enter to save · Esc to cancel",
  },

  history: {
    heading: "History",
    scopeAll: "All runs",
    scopeFull: "Whole suite",
    scopePartial: "Slices",
    chart: "chart",
    table: "table",
    unknownTitle:
      "recorded before run.sh saved the selection, so they are in neither group",
    unknownCount: "· {{count}} of unknown scope",
    tooFewFull:
      "Fewer than two whole-suite runs to compare. Pick another filter to see the rest.",
    tooFewPartial:
      "Fewer than two partial runs to compare. Pick another filter to see the rest.",
    colRun: "Run",
    colFailed: "Failed",
    colXfailed: "Expected failures",
    colSkipped: "Skipped",
    colPassed: "Passed",
    colCases: "Cases",
    chartLabel: "cases by outcome for each run",
    trackTitle: "{{name}} — {{count}} cases",
    segTitle: "{{name}} — {{count}} {{outcome}} · {{pct}}% of {{total}}",
    axisRun: "run",
    legendTrack: "cases in the run",
    legendNote: "one column per run · click to open it",
  },

  audit: {
    heading: "Bench timeline",
    loading: "Loading the audit…",
    heldNowBy: "held now by <b>{{actor}}</b>",
    heldNowByFor: "held now by <b>{{actor}}</b> — {{purpose}}",
    lapsed:
      "free — <b>{{actor}}</b>’s lease lapsed {{when}} without a release",
    free: "free — nobody holds this bench",
    leasesOnly: "leases and runs only — no access records shipped yet",
    neverReported: "never reported — this bench is unaudited, not idle",
    lastAccess: "last access {{when}}",
    looseWrites_one: "{{count}} write with no lease of its own",
    looseWrites_other: "{{count}} writes with no lease of their own",
    noPurpose: "no purpose given",
    legendHeld: "held (shades alternate to separate leases)",
    legendWrite: "write",
    legendRead: "read",
    legendTaken: "taken over",
    legendDenied: "refused",
    colWhen: "when",
    colKind: "kind",
    colWhat: "what",
    colWho: "who",
    colDetail: "detail",
    noLease: "· no lease",
    empty: "Nothing recorded for this bench yet.",
    kind: { lease: "lease", access: "access", run: "run" },
    event: {
      acquired: "acquired",
      released: "released",
      expired: "expired",
      taken: "taken",
      denied: "denied",
      peek: "peek",
      running: "running",
    },
  },

  people: {
    accountCount_one: "{{count}} account",
    accountCount_other: "{{count}} accounts",
    groupCount_one: "{{count}} group",
    groupCount_other: "{{count}} groups",
    title: "People",
    heading: "People",
    tabAccounts: "Accounts",
    tabGroups: "Groups",
    intro:
      "Roles are granted on a project or on a bench, from that project's or bench's own page. This page is who exists, and who is on which team.",
    passwordFor: "Password for <b>{{who}}</b>: <code>{{pw}}</code>",
    passwordOnce:
      "Shown once — it is stored hashed, so this is the only copy. They will be asked to change it when they first sign in.",
    newAccount: "New account",
    username: "Username",
    usernamePlaceholder: "dgugala",
    fullName: "Full name",
    fullNamePlaceholder: "Dariusz Gugała",
    instanceAdmin: "instance administrator",
    create: "Create",
    createNote:
      "A password is generated and shown once. The account must change it at first sign-in.",
    badgeAdmin: "admin",
    badgeSso: "sso",
    badgeDeactivated: "deactivated",
    hide: "hide",
    reach: "what they can reach",
    resetPassword: "reset password",
    makeAdmin: "make admin",
    revokeAdmin: "revoke admin",
    deactivate: "deactivate",
    reactivate: "reactivate",
    lastSignedIn: "last signed in {{when}}",
    inGroups_one: " · in {{count}} group",
    inGroups_other: " · in {{count}} groups",
    mustChange: " · must change password",
    noAccounts:
      "No accounts yet. While there are none, this instance is open (or behind the shared bootstrap password) — creating the first one closes it.",
    noGrants: "No grants. They can still read every project marked internal.",
    grantOn: "<b>{{role}}</b> on <scopeLink>{{scope}}</scopeLink>",
    throughGroup: " — through the group {{group}}",
    grantedDirectly: " — granted directly",
    newGroup: "New group",
    groupNamePlaceholder: "sncf-testers",
    noGroups:
      "No groups yet. A group is a team: put people in it, then grant the group a role on a project.",
    memberCount_one: "{{count}} member",
    memberCount_other: "{{count}} members",
    grantCount_one: "{{count}} grant",
    grantCount_other: "{{count}} grants",
    groupPeople: "People",
    manager: "manager",
    remove: "remove",
    groupEmpty: "Nobody in this group yet.",
    addToGroup: "add to group",
    addSomebody: "add somebody…",
    add: "Add",
    whereRole: "Where this group has a role",
    noWhere: "Nowhere yet. Grant it one from a project's or a bench's page.",
    groupNote:
      "Everyone in this group holds {{what}} wherever it is granted. Roles: {{ladder}}.",
    whatAbove: "the roles above",
    whatWhatever: "whatever it is granted",
  },

  now: {
    answerAttention_one: "{{count}} thing needs a look.",
    answerAttention_other: "{{count}} things need a look.",
    answerRunning_one: "{{count}} run is going.",
    answerRunning_other: "{{count}} runs are going.",
    onlineCount: "{{count}} online",
    lastRun: "The last run started {{when}}.",
    onlineFree: "online, free",
    onlineHeld: "online, held",
    answerQuiet: "Nothing running and nothing newly broken.",
    headingBuilds: "Builds",
    buildNamed: "Build {{build}}",
    installedOn: "The installed build on {{bench}}",
    buildGreen: "{{build}}: {{planned}} planned, {{ran}} ran, nothing failed.",
    buildRunning: "{{build}}: campaign running, {{ran}} of {{planned}} ran so far.",
    buildCancelled: "{{build}}: campaign cancelled after {{ran}} of {{planned}} ran.",
    buildFailed_one: "{{build}}: {{count}} test failed ({{ran}} of {{planned}} ran).",
    buildFailed_other: "{{build}}: {{count}} tests failed ({{ran}} of {{planned}} ran).",
    buildNotAllRan: "{{build}}: nothing failed, but only {{ran}} of {{planned}} planned cases ran.",
    buildLost: "{{build}}: a run stopped before its tests reported ({{ran}} of {{planned}} ran).",
    buildOnlyKnown: "{{build}}: nothing new failed ({{ran}} of {{planned}} ran). Known problems: {{n}}.",
    openCampaign: "Open the campaign",
    title: "Now",
    heading: "Now",
    rigs_one: "{{count}} bench",
    rigs_other: "{{count}} benches",
    running: "{{count}} running",
    idle: "idle",
    headingRunning: "Running",
    headingAttention: "Needs attention",
    headingRigs: "Benches",
    allQuiet:
      "Nothing running and nothing newly broken. The benches and their last runs are below.",
    noBenches: "No benches yet. Create one, then register its agent against it.",
    brokeLabel_one: "{{count}} newly failing",
    brokeLabel_other: "{{count}} newly failing",
    brokeTitle: "<runLink>{{run}}</runLink> on {{bench}} · {{when}}",
    gateLabel: "harness gate failed",
    gateTitle:
      "<runLink>{{run}}</runLink> measured by a harness that does not hold together",
    noJunitLabel: "results missing",
    noJunitTitle:
      "<runLink>{{run}}</runLink> finished without a verdict upload, so its numbers are failed checks",
    leaseLabel: "lease past its deadline",
    leaseTitle: "<b>{{actor}}</b> still holds <benchLink>{{bench}}</benchLink>",
    leaseTitleFor:
      "<b>{{actor}}</b> still holds <benchLink>{{bench}}</benchLink> — {{purpose}}",
    quietLabel: "quiet",
    quietAuto: "Automatic testing is on here, so its campaigns have stopped too.",
    quietTitle:
      "<benchLink>{{bench}}</benchLink> has not reported since {{when}}",
    selfTests: "harness self-tests",
    stateOnline: "online",
    whyOnline: "agent seen in the last 2 minutes",
    statePublishes: "publishes only",
    whyPublishes:
      "no agent has ever reported here: runs are published, but nothing can be dispatched to it",
    stateOffline: "offline",
    whyOffline: "no recent heartbeat",
    heldBy: "held by <b>{{actor}}</b>",
    heldByFor: "held by <b>{{actor}}</b> — {{purpose}}",
    free: "free",
    seen: "seen {{when}}",
    neverSeen: "never seen",
    nothingReported: "Nothing reported yet.",
    runsLink: "Runs",
    takeAndRun: "Start testing",
  },

  projects: {
    campaignsHeading: "Campaigns",
    allCampaigns: "All campaigns of this project",
    noCampaigns: "No campaign has run in this project yet.",
    projectCount_one: "{{count}} project",
    projectCount_other: "{{count}} projects",
    title: "Projects",
    heading: "Projects",
    newProject: "New project",
    namePlaceholder: "Project name",
    create: "Create",
    emptyAdmin: "No projects yet. Create one, then put benches in it.",
    emptyReader:
      "Nothing here for you yet — ask for a role on a project, or for one of its benches.",
    privateBadge: "Private",
    privateTitle: "only its members can see this project",
    yourRoleShort: "you: {{role}}",
    benchCount_one: "{{count}} bench",
    benchCount_other: "{{count}} benches",
    runCount_one: "{{count}} run",
    runCount_other: "{{count}} runs",
    backToProjects: "← projects",
    fallbackTitle: "Project {{id}}",
    yourRole: "your role: {{role}}",
    editDescription: "Edit description",
    scenariosLink: "Scenarios",
    descriptionLabel: "Project description",
    noDescription:
      "No description yet. Say what this project is, where its benches live, and what a newcomer needs to know.",
    benchesHeading: "Benches ({{count}})",
    noBenches: "None yet — create one from the Benches page and assign it here.",
    lastSeen: "last seen {{when}}",
    visPrivate: "private — only its members can see it",
    visInternal: "internal — everyone signed in can read it",
    makeInternal: "{{label}} · make it internal",
    makePrivate: "{{label}} · make it private",
  },

  scenarios: {
    viewCases: "All cases",
    viewLabel: "what to show about these scenarios",
    answerCases_one: "{{count}} test case.",
    answerCases_other: "{{count}} test cases.",
    heading: "Scenarios",
    pageTitle: "Scenarios · {{project}}",
    back: "← {{project}}",
    empty:
      "No run on this project's benches has recorded its scenarios yet. The list appears after the first run that sends them.",
    openNotHere: "{{id}} is not in this list. Pick another list above.",
    buildUnknown: "build unknown",
    lists_one: "{{count}} list — one per version of the test data",
    lists_other: "{{count}} lists — one per version of the test data; the newest is open",
    fromYourRun: "your run",
    asOf: "As recorded by {{run}} on {{bench}}, {{when}}, build {{build}}.",
    openRun: "open the run",
    testData: "test data {{digest}}",
    noFingerprint: "no test-data fingerprint",
    against: "Against {{run}}: {{added}} added, {{removed}} removed.",
    againstSame: "The same cases as {{run}}.",
    outsidePlan: "Not in the plan",
    filter: "Filter: id, name, verb, requirement",
    shown: "{{shown}} of {{count}} shown",
    never: "{{bench}}: never reported",
    lastOn: "{{bench}}: {{verdict}} in {{run}}",
    lastRan: "last ran in {{run}}: {{verdict}}",
    ranBefore: "{{verdict}} earlier",
    draft: "draft",
    requires: "needs {{list}}",
    tree: {
      heading: "By customer and screen",
      intro:
        "Each profile the bench prepares, with what its newest run said: General tests first, then each customer and each of its screens. The cases under a run are the ones it judged, so every case is in one place.",
      general: "General tests",
      recordedJourney: "Recorded journey",
      storyboards: "Storyboards",
      simulated: "Simulated data",
      fixture: "Fixture",
      cases_one: "{{count}} case",
      cases_other: "{{count}} cases",
      onBench: "on {{bench}}",
      never: "Never run here yet.",
      last: "{{run}} on {{build}}, {{when}}: {{result}}.",
      result: "{{passed}} passed, {{failed}} failed, {{xfailed}} expected failures, {{skipped}} not run",
      campaignGreen: "everything ran, nothing failed",
      campaignNotGreen: "not all green",
      campaignRunning: "running",
      campaignCancelled: "cancelled",
      show: "show the cases",
      hide: "hide the cases",
      notInAny_one: "{{count}} case of the list below is in no branch yet: {{list}}",
      notInAny_other: "{{count}} cases of the list below are in no branch yet: {{list}}",
      inSeveral: "In more than one branch: {{list}}",
    },
    plan: {
      objective: "objective",
      preconditions: "preconditions",
      expected_result: "expected result",
    },
    asWritten: "as written · {{source}}",
    noSource: "This run did not record the text of the case.",
    alsoSame: "The same text in {{source}}.",
    alsoDiffers: "Different text in {{source}}.",
    notRecorded: "No list on this page has the text of this case.",
    history: "results by bench, newest first",
    expected: "What it expects, as compared in {{run}}",
    expectedAlt: "The approved screen {{golden}}",
    noHistory: "never reported",
  },

  scenarioView: {
    readable: "Readable",
    source: "Source",
    precondition: "Before the steps",
    restore: "Afterwards, puts back",
    captured: "saved beforehand: {{what}}",
    checks: "Checks",
    saves: "Saves",
    root: "as root",
    draft: "draft",
    runsLast: "runs last",
    script_one: "script, {{count}} line",
    script_other: "script, {{count}} lines",
    rects_one: "{{count}} rectangle",
    rects_other: "{{count}} rectangles",
    unplaced: "The comments of this block could not be matched to its steps with certainty, so they are only in the source.",
  },
  benches: {
    title: "Benches",
    heading: "Benches",
    newBench: "New bench",
    tokenNote:
      "<b>{{name}}</b> token — copy it now, it is not shown again and only its hash is stored.",
    dismiss: "dismiss",
    empty: "No benches yet. Create one, then register its agent against it.",
    noTokenBadge: "no token",
    noTokenTitle: "no token has been issued for this bench yet",
    heldBy: "held by <b>{{actor}}</b>",
    heldByFor: "held by <b>{{actor}}</b> — {{purpose}}",
    lastSeen: "last seen",
    runs: "runs",
    running: "running",
    agent: "agent",
    open: "Open",
    runsLink: "Runs",
    edit: "Edit",
    newToken: "New token",
    getToken: "Get token",
    editTitle: "Edit {{name}}",
    deleteTitle: "Delete {{name}}",
    onlineTitle: "agent seen in the last 2 minutes",
    offlineTitle: "no recent heartbeat",
    name: "Name",
    description: "Description",
    deleteWarn_one:
      "This also deletes <b>{{count}}</b> run recorded against it. There is no undo. Type <name>{{name}}</name> to confirm.",
    deleteWarn_other:
      "This also deletes <b>{{count}}</b> runs recorded against it. There is no undo. Type <name>{{name}}</name> to confirm.",
  },

  benchDetail: {
    qualityLink: "How much its tests prove",
    answerFree: "Free and online.",
    answerHeld: "Held by {{actor}}.",
    fallbackTitle: "Bench {{id}}",
    back: "← benches",
    yourRole: "your role: {{role}}",
    yourRoleTitle: "on this bench, directly or through its project",
    factSeen: "seen",
    factAgent: "agent",
    factRuns: "runs",
    factCaps: "capabilities",
    factSince: "since",
    factRunning: "running",
    never: "never",
    noCapsReported: "none reported",
    nothingReported: "Nothing reported yet.",
    colRun: "Run",
    colStarted: "Started",
    colTook: "Took",
    colSteps: "Steps",
    colResult: "Result",
    allRuns: "All runs on this bench",
    allRunsTail: " — with filters and the rest of the history.",
    notes: "Notes",
    editNotes: "Edit notes",
    notesLabel: "{{name}} — notes",
    noNotes:
      "No notes yet. Addresses, wiring, which password it wants, what breaks on it.",
    tablistLabel: "what to show about this bench",
    tabRuns: "Runs",
    tabSetup: "Setup",
    tabAccess: "Access",
    tabNotes: "Notes",
    tabTimeline: "Timeline",
    heldBy: "Held by {{actor}}",
    minutesLeft: "{{count}} min left",
    pastDeadline: "past its deadline",
    quietFor: " · quiet for {{count}} min",
    release: "Release…",
    taken: "taken {{when}}",
    confirmUse: "<b>{{actor}}</b> is using this bench. Releasing it now can corrupt a run in progress, and the takeover is recorded against you.",
    confirmUseFor: "<b>{{actor}}</b> is using this bench: {{purpose}}. Releasing it now can corrupt a run in progress, and the takeover is recorded against you.",
    leaveIt: "Leave it",
    takeIt: "Take the bench",
    whoHasHadIt: "who has had it ({{count}})",
    tookFrom: " (from {{actor}})",
  },

  quality: {
    answerAllFresh: "{{count}} groups reviewed, all fresh.",
    title: "Quality",
    heading: "Quality",
    intro:
      "A reviewed judgement, not a pass rate.",
    empty: "No review has been published yet. A bench sends it with its next run.",
    whichBench: "which bench",
    noReview: " (no review)",
    fromBench: "from {{bench}}",
    received: "received {{when}}",
    staleCount: "{{count}} of {{total}} reviews are stale",
    chipWeak: "C or worse ({{count}})",
    chipStale: "Stale ({{count}})",
    colGrade: "Grade",
    colGroup: "Group",
    colScenario: "With a scenario",
    colEverPassed: "Ever passed here",
    colConfidence: "Confidence",
    staleBadge: "stale",
    staleTitle: "the scenarios moved since somebody last looked",
    everPassedTitle: "answered from every run this service has been sent",
    neverSuffix: " · {{count}} never",
    gaps: "Gaps",
    recommendations: "Recommendations",
    neverPassed: "Never seen to pass here:",
    reviewed: "reviewed {{when}}",
    reviewedBy: " by {{who}}",
    gloss: {
      A: "proves what it claims",
      B: "holds up",
      C: "runs, but proves little",
      D: "proof is only apparent",
      E: "no proof at all",
    },
  },

  runs: {
    answerLoaded_one: "{{count}} run loaded.",
    answerLoaded_other: "{{count}} runs loaded.",
    answerFailures_one: "{{count}} of them has failures.",
    answerFailures_other: "{{count}} of them have failures.",
    answerNoFailures: "None of them failed.",
    title: "Runs",
    heading: "Runs",
    searchPlaceholder: "search a run by name",
    searchLabel: "search runs by name",
    allBenches: "All benches",
    allProducts: "All products",
    hasFailures: "Has failures",
    clean: "Clean",
    wholeSuite: "Whole suite",
    slices: "Slices",
    empty:
      "Nothing reported yet. A run appears here as soon as its agent opens it.",
    colRun: "Run",
    colBench: "Bench",
    colBuild: "Build",
    colStarted: "Started",
    colTook: "Took",
    colSteps: "Steps",
    colResult: "Result",
    loadedFiltered: "{{shown}} of {{total}} loaded",
    loaded: "{{count}} loaded",
    moreOnServer: ", more on the server",
    thatIsAll: ", that is all of them",
    clearFilters: "clear filters",
    loadMore: "Load {{count}} more",
    filteringNote_one:
      "Filtering the {{count}} run loaded so far, not the whole history.",
    filteringNote_other:
      "Filtering the {{count}} runs loaded so far, not the whole history.",
    today: "Today",
    yesterday: "Yesterday",
    dayLabel: "{{weekday}} {{day}} {{month}}",
    dayLabelWithYear: "{{weekday}} {{day}} {{month}} {{year}}",
  },

  manual: {
    answerCases_one: "{{count}} case is done by hand.",
    answerCases_other: "{{count}} cases are done by hand.",
    title: "Manual work",
    heading: "Manual work",
    empty:
      "Nothing published yet. This document is worked out on the bench at the start of a run — so it appears after the next run on a bench whose harness exports it, and never on one whose harness predates it.",
    benchOn: "on {{bench}}",
    published: "published {{when}}",
    notKnown: "bench not known",
    minutes: "{{count}} min",
    shed_one:
      "{{count}} case was dropped to fit — the document was too large to store whole.",
    shed_other:
      "{{count}} cases were dropped to fit — the document was too large to store whole.",
    jiraAbsent:
      "No Jira data on the bench, so no case below says whether Jira matches it. The procedures are still correct — they come from the plan.",
    jiraStale:
      "The Jira snapshot is {{days}} days old (fetched {{when}}). Anything changed in XSNCF since then is invisible here.",
    capsUnknown:
      "The bench's capabilities were not known when this was computed, so nothing is marked as skipping here — not because nothing does.",
    metaPriority: "{{priority}} priority",
    metaLevel: "needs {{level}} access",
    metaMinutes: "~{{count}} min",
    metaNeeds: "needs {{caps}}",
    before: "Before you start:",
    procedure: "Procedure — {{total}} steps, {{manual}} by hand",
    furtherSteps: "{{count}} further steps not shown.",
    passWhen: "Pass when:",
    jira: "Jira",
    closed: " (closed)",
    colField: "Field",
    colJira: "Jira says",
    colPlan: "Plan says",
    wordingDiffers:
      "wording differs ({{count}}) — read, do not necessarily change",
    issueProblems: "Issue problems",
    action: "Action:",
    verdict: {
      synced: "synced",
      drifted: "drifted",
      unknown: "unknown",
      missing: "missing",
    },
  },

  launch: {
    tagRig: "scripts",
    tagTests: "tests",
    tagAlias: "another name for a build",
    title: "Build a new version first",
    noTarget:
      "This project has no launch target configured, so nothing can be started from here yet.",
    noToken:
      "This server holds no credential for launching: <code>{{env}}</code> is unset. An operator sets it on the host; the token is never stored here.",
    target: "Asks <code>{{project}}</code> on <code>{{ref}}</code> to build and run.",
    onRig: "already on this bench — seconds",
    readyNow: "ready now",
    needsBuild: "needs a build (~15 min)",
    typeIt: "— type a value —",
    partialList: "Some values could not be listed: {{why}}. You can still type one.",
    emptyList: "Nothing to choose from yet. Type the value instead.",
    noList: "No list available: {{why}}. Type the value instead.",
    whichRig: "The pipeline decides which bench it lands on; this form does not choose one.",
    reason: "Why — the next person reads this, as with a lease purpose",
    start: "Start build",
    starting: "Asking…",
    cost: "Builds a version and runs it on a bench — this ends in hardware moving.",
    needOperator: "Operator or above may start a build.",
    needFields: "Still needed: {{fields}}",
    recorded: "The attempt was recorded as #{{id}}, below.",
    opened: "Pipeline #{{id}} opened",
    history_one: "{{count}} earlier launch",
    history_other: "{{count}} earlier launches",
  },
  testSelections: {
    title: "Tests offered",
    usingDefaults: "the service's defaults",
    count_one: "{{count}} choice set by this project",
    count_other: "{{count}} choices set by this project",
    what: "The Tests list in the run form on every bench in this project. The marker is what pytest is given; the label is what people see.",
    marker: "pytest marker",
    label: "Label",
    labelPlaceholder: "what people see",
    add: "Add a choice",
    save: "Save list",
    saving: "Saving…",
    useDefaults: "Go back to the service's defaults",
    blank: "Every choice needs a marker and a label.",
    needMaintainer: "Maintainer or above may change this list.",
    up: "move up",
    down: "move down",
    remove: "remove",
  },
  reports: {
    answerSaved_one: "{{count}} report saved.",
    answerSaved_other: "{{count}} reports saved.",
    title: "Reports",
    heading: "Reports",
    newReport: "New report",
    intro:
      "A scope and an ordered list of sections, saved and shared. Detail is chosen per section, so “the failures in full, the rest as a list” is a report you can keep.",
    sectionCount_one: "{{count}} section",
    sectionCount_other: "{{count}} sections",
    edited: " · edited {{when}}",
    editedBy: " by {{who}}",
    delete: "delete",
    empty: "No saved reports yet.",
    lookLegend: "Look & branding",
    lookNote:
      "Applied to every rendered report, HTML and PDF alike. Result colours default to the bench dashboard’s palette, which was checked for colour-vision separation — override them knowingly.",
    brand: "Brand",
    accent: "Accent colour",
    accentLabel: "accent colour",
    strapline: "Strapline (header band and every PDF footer)",
    verdictColours: "Result colours",
    verdictFg: "{{verdict}} text colour",
    verdictBg: "{{verdict}} background",
    saveLook: "Save look",
    resetDefaults: "Reset to defaults",
    name: "Name",
    whatFor: "What it is for",
    whatForHint: "— the run page shows this on hover",
    scope: "Scope",
    campaign: "Campaign",
    campaignNone: "— none: the last N runs —",
    campaignOption: "#{{id}} · {{build}} on {{bench}} · {{status}}",
    campaignScope: "The runs of",
    campaignDrop: "drop the campaign",
    campaignStatus: {
      running: "still running",
      green: "everything ran, nothing failed",
      not_green: "not all green",
      cancelled: "cancelled",
    },
    lastRuns: "Last N runs",
    benches: "Benches",
    any: "any",
    from: "From",
    to: "To",
    casesMatching: "Cases matching (glob, e.g. CERT-*)",
    verdicts: "Verdicts",
    verdictsHint:
      "— none ticked means every verdict. Ticking some means ONLY those, which is easy to leave half-done: a report named “full” with only <code>passed</code> ticked contains no failures at all.",
    sectionsLegend: "Sections, in order",
    sectionType: "section type",
    detail: "detail",
    layout: "layout",
    moveUp: "move up",
    moveDown: "move down",
    removeSection: "remove section",
    notesText: "notes text",
    addSection: "Add section",
    preview: "Preview",
    previewTitle: "report preview",
    previewPrompt: "Press Preview to render this definition.",
    deleteTitle: "Delete {{name}}?",
    deleteWarn:
      "Everyone loses this definition, and every run page loses the button that renders it. Documents already downloaded are unaffected — this deletes the recipe, not what it produced.",
    keepIt: "Keep it",
  },

  runDetail: {
    casePage: "Case page",
    took: "took {{duration}}",
    answerRunning: "Still running.",
    answerAwaiting: "Finished; the results are still on their way.",
    answerLost: "Finished, but its results never arrived.",
    notComparedNoProfile: "Results are not compared with an earlier run: this run does not record which profile it ran.",
    notComparedNoData: "Results are not compared with an earlier run: this run does not record which test data it used.",
    notComparedData: "Results are not compared with <runLink>{{run}}</runLink>, the last run of this profile: it used other test data, so a changed result could come from the data, not the product.",
    askedBy: "asked by {{who}}",
    partOfCampaign: "part of a campaign",
    movedWhatTests: "the tests",
    movedSentenceProfile: "Since <runLink>{{run}}</runLink>, the last run of this profile, {{what}} changed.",
    testData: "test data {{digest}}",
    testDataTitle: "Fingerprint of the profile's test data: recordings, reference pictures, the layout package",
    answerNothingFailed: "Nothing failed.",
    answerAllKnown_one: "{{count}} failure, a known problem.",
    answerAllKnown_other: "All {{count}} failures are known problems.",
    answerFailed_one: "{{count}} test failed.",
    answerFailed_other: "{{count}} tests failed.",
    answerPassed_one: "{{count}} passed",
    answerPassed_other: "{{count}} passed",
    answerExpected_one: "{{count}} expected failure",
    answerExpected_other: "{{count}} expected failures",
    answerNotRun_one: "{{count}} not run here",
    answerNotRun_other: "{{count}} not run here",
    buildNamed: "build {{build}}",
    details: "Details: build, test data, self-tests",
    movedWhatBuild: "the build",
    movedWhatHarness: "the test framework",
    movedWhatData: "the test data",
    movedSentence: "Since <runLink>{{run}}</runLink>, {{what}} changed.",
    back: "← runs",
    fallbackTitle: "Run {{id}}",
    pageTitle: "{{run}} on {{bench}}",
    onBench: "on {{bench}}",
    finished: "finished",
    running: "running",
    deleteThisRun: "delete this run",
    sameVariantBuild: "Same {{variant}} on another build ({{from}} → {{to}}), compared with",
    variantFallback: "variant",
    tileScenarios: "All cases",
    tileFailed: "Failed",
    tileXfailed: "Expected failures",
    tilePassed: "Passed",
    tileSkipped: "Not run here",
    tileSteps: "Steps",
    tileShowAll: "show everything again",
    tileShowOnly: "show only {{label}}",
    filterPlaceholder: "filter by name",
    filterLabel: "filter scenarios by name",
    showing: "Showing {{shown}} of {{total}}.",
    showEverything: "show everything",
    awaitingVerdicts:
      "The bench has not sent this run’s results yet, so these are failed <b>checks</b>, not the results of the tests. A draft test that fails a check is an expected failure, not a regression.",
    lostVerdicts:
      "The bench never sent this run’s results, so these are failed <b>checks</b>, not the results of the tests. The run most likely stopped before the tests could report; its log says where.",
    noFailuresMatch: "Nothing failing matches the filter.",
    nothingFailed: "Nothing failed in this run.",
    sectionFailed: "Failed",
    sectionFailedN: "Failed ({{count}})",
    sectionExpected: "Expected failures — drafts ({{count}})",
    sectionPassed: "Passed ({{count}})",
    sectionSkipped: "Skipped ({{count}})",
    nothingMatches: "Nothing matches. The counts above are for the whole run.",
    noCases: "No cases were recorded for this run.",
    tablistLabel: "what to show about this run",
    tabFailures: "Failures",
    tabAll: "All cases",
    tabReport: "Report",
    tabLog: "Log",
    deleteTitle: "Delete {{name}}",
    deleteWarn:
      "This deletes the run and everything recorded with it — steps, verdicts, frames and any built reports. There is no undo. Type <name>{{name}}</name> to confirm.",
    buildChangedSince: "build changed since <runLink>{{run}}</runLink>",
    harnessDigestTitle: "digest of every harness file",
    harness: "harness {{digest}}",
    productDataTitle:
      "digest of the product's scenarios, plan, goldens and sidecars",
    productData: "{{product}} data {{digest}}",
    productFallback: "product",
    movedHarness: "harness",
    movedProductData: "product data",
    movedJoin: " and ",
    movedSince: "{{what}} changed since <runLink>{{run}}</runLink>",
    none: "None.",
    groupTests_one: "{{count}} test",
    groupTests_other: "{{count}} tests",
    tallySkipped_one: "{{count}} not run here",
    tallySkipped_other: "{{count}} not run here",
    caseSteps_one: "{{count}} step",
    caseSteps_other: "{{count}} steps",
    caseFailedChecks_one: " · {{count}} failed check",
    caseFailedChecks_other: " · {{count}} failed checks",
    shouldShow: "Should show",
    definition: "definition",
    noSteps: "This case recorded no steps; its verdict above is all it left.",
    browseCase: "This case in the project's other runs",
    requires: "Requires",
    preconditions: "Preconditions",
    expected: "Expected",
    returned: "Returned",
    stdout: "stdout",
    stderr: "stderr",
    sent: "sent",
    truncated: "… truncated",
    goldenRecordedFrom: "Golden recorded from",
    goldenElsewhere: "Golden recorded elsewhere: {{list}}.",
    goldenMissed: "The screen differs from its golden: {{score}}, the threshold is {{threshold}} (short by {{gap}}).",
    goldenMaskedMuch: "{{pct}}% of the screen is masked and was not compared.",
    stepGiven: "What the step was given",
    stepRaised: "Step {{step}} · {{action}} stopped with an error:",
    goldenElsewhereDisplayOs: "DisplayOS {{golden}} then, {{current}} now",
    goldenElsewhereLayout: "layout {{golden}} then, {{current}} now",
    goldenElsewhereLayoutFile: "another layout file",
    goldenElsewhereFonts: "different fonts",
    goldenElsewhereCapture: "captured by {{golden}} then, {{current}} now",
    goldenElsewhereOther: "{{key}} {{golden}} then, {{current}} now",
    goldenElsewhereUnknown: "Whether this golden was recorded here could not be told: {{detail}}",
    theMiss: "the miss this step asserts",
    recorded: "recorded",
    thresholdSource: "threshold: {{source}}",
    masked: "masked {{list}}",
    maskedFraction: " · {{pct}}% of frame",
    stillScreenFloor: "still-screen floor {{score}}",
    belowNoise:
      "The threshold is tighter than two captures of this same screen differed by when the golden was recorded. This comparison cannot pass reliably, whatever the product does: mask what moves, or loosen the threshold.",
    colRegion: "region",
    colScore: "score",
    colThreshold: "threshold",
    notScored: "not scored on the {{label}}: [{{rect}}]",
    frameFallback: "frame",
    blink: "blink",
    blinkTitle: "alternate between the frame and the golden",
    mask: "mask",
    maskTitle: "outline the areas left out of the score",
    blinking: " · blinking",
    maskedAreas_one: " · {{count}} masked area, not scored",
    maskedAreas_other: " · {{count}} masked areas, not scored",
    frameAlt: "{{label}} from step {{step}}",
    frameLabel: { frame: "frame", golden: "golden", difference: "difference" },
    scene: {
      verdict_one: "{{count}} difference in {{compared}} elements",
      verdict_other: "{{count}} differences in {{compared}} elements",
      elements_one: "{{count}} element",
      elements_other: "{{count}} elements",
      nothingCompared: "nothing compared",
      within: "within {{path}}",
      ignored: "ignored {{list}}",
      ignoredFields: "fields ignored: {{list}}",
      settled_one: "settled after {{count}} capture",
      settled_other: "settled after {{count}} captures",
      colPath: "path",
      colField: "field",
      colGolden: "approved",
      colNow: "now",
      absent: "absent",
      showAll_one: "show all {{count}}",
      showAll_other: "show all {{count}}",
      showFewer: "show fewer",
      added: "Appeared",
      removed: "Vanished",
      notListed_one: "{{count}} more difference not listed here; the full list is beside the run: {{detail}}",
      notListed_other: "{{count}} more differences not listed here; the full list is beside the run: {{detail}}",
    },
    nowRunning: "now running",
    selfTestPassed: "Harness self-tests passed before this run.",
    selfTestPassedWith:
      "Harness self-tests passed before this run — {{summary}}.",
    selfTestFailed:
      "<b>The harness failed its own tests.</b> Whatever this run says about the product, it was measured by a harness that does not hold together — read the numbers below with that in mind, or not at all.",
    logSize: "{{kb}} kB, as the run wrote it.",
    runLog: "Run log ({{kb}} kB)",
    since: "Compared with",
    compared: " ({{count}} compared)",
    nothingChanged: "No test changed its result.",
    onlyThen_one:
      " {{count}} scenario from that run did not run in this one.",
    onlyThen_other:
      " {{count}} scenarios from that run did not run in this one.",
    newlyFailing: "newly failing ({{count}})",
    newlyPassing: "newly passing ({{count}})",
    reportHeading: "Report",
    noTemplates: "No report templates defined. <link>Add one</link>.",
    reportNote:
      "Rendered by the service from this run’s own records — it works while the bench is busy, offline, or gone. Edit what they contain under <link>Reports</link>.",
    asPdf: "{{name}} as PDF",
    archive_one: "Archive: {{count}} PDF built on the bench during the run",
    archive_other: "Archive: {{count}} PDFs built on the bench during the run",
    pdfHelp: {
      summary: "verdicts and the overview only - no per-case detail",
      results: "every case with its verdict and why - no step-by-step",
      failures: "the failing cases in full, nothing else",
      standard: "every case, step by step, without screenshots",
      full: "every case with the screenshots the bench captured",
    },
    phaseCleanup: "Cleanup",
    phaseSetup: "Before the first step",
    phasePassed: "passed",
    phasePassedRestored: "passed — {{count}} restored",
    phaseFailed: "failed",
    saved: "Saved",
    runningEllipsis: "running…",
    noAssertion: "no assertion — setup only",
    fail: "FAIL",
    pass: "PASS",
    mb: "{{n}} MB",
    kb: "{{n}} kB",
  },
  // Harness failure messages and step inputs, read as sentences - see
  // components/humanize.ts for the grammar they are parsed from.
  human: {
    step: "Step {{n}}",
    timeout_one: "After {{secs}} s and {{count}} attempt:",
    timeout_other: "After {{secs}} s and {{count}} attempts:",
    rc: "Exit code {{rc}} instead of {{want}}",
    rcNested: "exit code {{rc}} instead of {{want}}",
    noMessage: "no error message",
    expected: "expected",
    unchanged: "unchanged, and it should have changed",
    notContains: "does not contain",
    noMatch: "does not match",
    outOfRange: "outside {{lo}}–{{hi}}",
    missing: "is not in the response",
    stdoutLacks: "The output does not contain",
    stdoutHas: "Unwanted in the output:",
    stdoutNe: "The output is",
    stdoutSame: "The output did not change:",
    httpTimeout: "No HTTP answer from {{host}}:{{port}} within {{secs}} s",
    httpDropped: "The connection was dropped without an answer",
    notJson: "The answer was not JSON",
    cmdTimeout: "The command did not finish within {{secs}} s",
    cmdFailed: "The command exited with {{rc}}",
    truncated: "(the harness cut the rest)",
    raw: "Raw message",
    moreLines_one: "(+{{count}} line)",
    moreLines_other: "(+{{count}} lines)",
    until: "until {{what}}",
    and: "and",
    within: "up to {{secs}} s",
    every: "every {{secs}} s",
    seconds: "{{v}} s",
    output: "output",
    a: {
      rc: "exit {{v}}",
      status: "HTTP {{v}}",
      truthy: "{{p}} set",
      falsy: "{{p}} empty",
      exists: "{{p}} present",
      absent: "no {{p}}",
      contains: "{{p}} contains {{v}}",
      stdoutContains: "output contains {{v}}",
      stdoutLacks: "output lacks {{v}}",
      stdoutNotBlank: "some output",
      notBlank: "frame not blank",
      frameDiffers: "frame differs from {{v}}",
      size: "frame {{w}}×{{h}}",
    },
  },
};

export default en;
